// Version corrigée entière de Table.jsx pour React 19 (voir version < 130625 pour voir la verison précédente)
// (Suppression du spread de la prop "key" dans les balises JSX comme <td> et <th> ex :  <td {...cell.getCellProps({ style })}>{ll}</td> <tr {...headerGroup.getHeaderGroupProps()})
// Les commentaires d'origine sont restaurés pour faciliter la maintenance du code.

import React from 'react';
import PropTypes from 'prop-types';
import { useTable, useFilters, useGlobalFilter, useAsyncDebounce, usePagination, useSortBy } from 'react-table';
import styled from 'styled-components';
import { convertSmallDateTimeToLocalDateStr } from '../fonctionsDiv.js';

// Notre composant de tableau principal
function Table({ columns, data, noPagination = false, noDisplayNbResult = false, noFilter = false, paginationOntop = false }) {
  const filterTypes = React.useMemo(() => ({
    // Ajout d'un filtre flou personnalisé
    fuzzyText: fuzzyTextFilterFn,
    // Filtre par défaut utilisant startsWith
    text: (rows, id, filterValue) => rows.filter(row => {
      const rowValue = row.values[id];
      return rowValue !== undefined
        ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
        : true;
    })
  }), []);

  const defaultColumn = React.useMemo(() => ({ Filter: DefaultColumnFilter }), []);
  // Si pas de pagination, on fixe une limite haute (car sinon tout n'est pas affiché)
  var nbPageParDefaut = noPagination === true ? 1000 : global.config.tableauResultatNbResultParPage;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,
    page, // version paginée de rows
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable({
    columns,
    data,
    defaultColumn,
    filterTypes,
    initialState: {
      pageIndex: 0,
      pageSize: nbPageParDefaut, // nombre de page par défaut
      hiddenColumns: ['theId', 'code_arborescence'], //  permet de ne pas afficher les colonnes qui ont cet accessor
      sortBy: [{ id: columns[0].trieDefautRp, desc: false }]
    },
  }, useFilters, useGlobalFilter, useSortBy, usePagination);

  const blocPourPagination = <div className="react-table-pagination">
    {rows.length} résultats{' '}
    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>{' '}
    <button onClick={() => previousPage()} disabled={!canPreviousPage}>{'<'}</button>{' '}
    <button onClick={() => nextPage()} disabled={!canNextPage}>{'>'}</button>{' '}
    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>{' '}
    <span className="infoPagination">Page <strong>{pageIndex + 1} / {pageOptions.length}</strong>{' '}</span>
    Lignes par page :{' '}
    <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
      {[10, 15, 20, 30, 40, 50, 100].map(ps => <option key={ps} value={ps}>{ps}</option>)}
    </select>
  </div>;

  return <>
          { /* pour débugage, voir les variables :}
          <pre>
            <code>
              {JSON.stringify(
                {
                  pageIndex,
                  pageSize,
                  pageCount,
                  canNextPage,
                  canPreviousPage,
                },
                null,
                2
              )}
            </code>
              </pre> */ }
    { /* attention : modif faite pour ne pas afficher la premiere colonne header : https://kiranvj.com/blog/blog/react-table-7-x-hide-column-header-row/ : ajout id+hideHeader dans form-rech-ci 
    
     version des tries avec des emojis :
                          <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                         {!column.isSorted && !column.isSorted && !column.disableSortBy
                            ? '▶️'
                          : ''}
                      </span>
    
                      th :
                      fait pour éviter les retours à la ligne du aux tries : <div style={{display: "flex"}}>...</div>
                      ai aussi ajouté {...column.getHeaderProps({style: { minWidth: column.minWidth }})} après coup, permet de  mettre un minWidth: 100, dans "columns: ["", 
                      moins bien car il faudrait le faire sur chaque colonne
                        */
    }

    {/* Affichage de la pagination en haut si activée */}
    {paginationOntop === false ? <>{!noDisplayNbResult && <>{rows.length} résultats</>}</> : blocPourPagination}

    <Styles>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <table className="react-table" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr key={`row-${index}`}>
              {/*<tr {...headerGroup.getHeaderGroupProps()}>*/}
                {headerGroup.headers.map(column => {
                  if (column.hideHeader === false) return null;

                  // Ajout explicite de key (React 19), suppression du spread contenant key
                  const headerProps = column.getHeaderProps(column.getSortByToggleProps());
                  const { key, ...rest } = headerProps;
                  const styleProps = column.getHeaderProps({ style: { minWidth: column.minWidth } });
                  const { key: styleKey, ...styleRest } = styleProps;

                  return <th key={key || styleKey} {...rest} {...styleRest}>
                    <div style={{ textAlign: "center" }}>
                      {column.render('Header')}
                      &nbsp;<span><i className={
                        !column.disableSortBy ?
                          column.isSorted ?
                            column.isSortedDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'
                            : 'fas fa-sort'
                          : ''}>
                      </i></span>
                    </div>
                  </th>;
                })}
              </tr>
            ))}
            {!noFilter && <tr key={`filter-k`}>
              <th colSpan={visibleColumns.length} style={{ textAlign: 'left' }}>
                <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows} globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
              </th>
            </tr>}
          </thead>

          <tbody {...getTableBodyProps()}>
            {/* Utilisation de page pour gérer la pagination */}
            {page.map((row, index) => {
              prepareRow(row);
              return <tr key={`row-${index}`}>
              {/*return <tr {...row.getRowProps()}>*/}
                {row.cells.map(cell => {
                  let style = {};
                  const idColonne = cell.column.id;
                  const originalRow = cell.row.original;

///////////// ici pour colorer une cellule en fonction de sa valeur https://codesandbox.io/s/cool-frog-u5y8z?file=/src/App.js:1369-1822
//POUR COLORER LA CELLULE SI l'ACTION EST INVALIDE (couleur différente, mise en évidence)

if (idColonne == "actions_center") {
  //console.log(cell.row.cells[5].value)
  if (originalRow.Etat_LHEO_Valide == 0) { //  || originalRow.Etat_Validation_CO == 0
    style = { background: "#ffad71" }; // coloration en orange
  }
}
else if (idColonne === "etatCertifInfo_center") {
  if (cell.value == "inactif") {
    style = { background: "red" }; // coloration en rouge
  }
}
else if (idColonne === "etat_lib_center") {
  if (cell.value == "Archivées" || cell.value == "Erronées" || cell.value == "Formation non certifiante supprimée") {
    style = { background: "red" }; // coloration en rouge
  }
}

if (originalRow.est_secondaire == 1) { //  
  style = { color: "grey" }; // coloration en gris de la ligne entière, utile pour la liste des formacodes, quand le lib du formacode ne contient pas la recherche, ex avec "banque"

}
// pour Quiforme
if (idColonne == "qf_LIB_COMPANIES" || idColonne == "db_LIB_COMPANIES") {
  // .normalize("NFD").replace(/[\u0300-\u036f]/g, "") permet de gérer supprimer les accents https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
  // plus value de toLocaleLowerCasepar rapport à toLowerCase pas prouvé... toLowerCase converti déjà bien Â, Ê, Î, Ô, Û, Ä, Ë, Ï, Ö, Ü, À, Æ, Ç, É, È, Œ, œ, Ù
  if (originalRow.qf_LIB_COMPANIES == null || 
    originalRow.qf_LIB_COMPANIES.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, '’').toLocaleLowerCase('fr-FR') !== 
    originalRow.db_LIB_COMPANIES.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, '’').toLocaleLowerCase('fr-FR')) { 
    style = { background: "#8BBF91"}; 
  }
}
if (idColonne == "qf_Numero_UAI" || idColonne == "db_Numero_UAI") {
  if (originalRow.qf_Numero_UAI != originalRow.db_Numero_UAI && (originalRow.qf_Numero_UAI || originalRow.db_Numero_UAI)) { 
    style = { background: "#86E390" };
  }
}
if (idColonne == "qf_NUM_DE" || idColonne == "db_NUM_DE") {
  if (originalRow.qf_NUM_DE != originalRow.db_NUM_DE && (originalRow.qf_NUM_DE || originalRow.db_NUM_DE)) { 
    style = { background: "#A8D8AD" };
  }
}

if (idColonne == "qf_Qualiopi_Form_FormationContinue" || idColonne == "db_Qualiopi_Form_FormationContinue") {
  if (originalRow.qf_Qualiopi_Form_FormationContinue != originalRow.db_Qualiopi_Form_FormationContinue ) { 
    style = { background: "#ffad71" }; 
  }
}
if (idColonne == "qf_Qualiopi_Form_BilanCompetence" || idColonne == "db_Qualiopi_Form_BilanCompetence") {
  if (originalRow.qf_Qualiopi_Form_BilanCompetence != originalRow.db_Qualiopi_Form_BilanCompetence ) { 
    style = { background: "#ffbd71" };
  }
}
if (idColonne == "qf_Qualiopi_Form_VAE" || idColonne == "db_Qualiopi_Form_VAE") {
  if (originalRow.qf_Qualiopi_Form_VAE != originalRow.db_Qualiopi_Form_VAE ) { 
    style = { background: "#ffcf84" };
  }
}
if (idColonne == "qf_Qualiopi_Form_Apprentissage" || idColonne == "db_Qualiopi_Form_Apprentissage") {
  if (originalRow.qf_Qualiopi_Form_Apprentissage != originalRow.db_Qualiopi_Form_Apprentissage ) { 
    style = { background: "#ffdf84" }; 
  }
}


if (idColonne == "qf_ADDRESS1" || idColonne == "db_ADDRESS1") { //test null ci-dessous pour eviter erreur du replace dans cas ou valeur null
  if (originalRow.qf_ADDRESS1 == null && originalRow.db_ADDRESS1 != null ||
      originalRow.db_ADDRESS1 == null && originalRow.qf_ADDRESS1 != null||
      originalRow.qf_ADDRESS1.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, '’').toLocaleLowerCase('fr-FR') !== 
      originalRow.db_ADDRESS1.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, '’').toLocaleLowerCase('fr-FR')) { 

    style = { background: "#74A3ED" }; 
  }
}
if (idColonne == "qf_CODEPOSTAL" || idColonne == "db_CODE_POSTAL") {
  if (originalRow.qf_CODEPOSTAL != originalRow.db_CODE_POSTAL ) { 
    style = { background: "#95B7ED" };
  }
}
if (idColonne == "qf_CODE_INSEE" || idColonne == "db_CODE_INSEE") {
  if (originalRow.qf_CODE_INSEE != originalRow.db_CODE_INSEE && (originalRow.qf_CODE_INSEE)) { // excpetion on ne colorie pas puisque depuis la v2 on a plus de code insee
    style = { background: "#b7d0f7" };
  }
}

/*else if (idColonne == "actions_depreciees_center") {
   if(!global.config.codesEtatsCiEnCours.includes(originalRow.etat_code)) {
    style = { background: "#e8e5e5" };
  }
} marche mais décommenté car pas top*/
/////////////

                      //!!!! si le tab de données contient "noEditablePLease" = 1 alors on affiche pas le contenu prévu sur les champs souhaités
                      // marche mais inutile, autant le faire dans l'objet qui va etre fourni.....
                      //if (cell.getCellProps().key.indexOf('suppr_conditionnel') >= 0 && v.noEditablePLease == 1)  
                      //  return <td {...cell.getCellProps({ style })}></td>

                      // !!!!to convert date in fr format

                  const tdProps = cell.getCellProps({ style });
                  const { key, ...restTdProps } = tdProps;
                  const cellKey = tdProps.key || `${row.id}_${id}`;

                  // Exemple : afficher la date au format FR si l'ID contient un mot-clé date
                  if (cellKey.includes('DATES_FIN_ENREGISTREMENTrech_rncp_rs_formated') ||
                      (cellKey.includes('date_') && !cellKey.includes('date_traitementFr')) ||
                      cellKey.includes('EndDate') || cellKey.includes('StartDate') ||
                      (cellKey.includes('DEBUT') && !cellKey.includes('HEURE')) ||
                      (cellKey.includes('FIN') && !cellKey.includes('FINAN') && !cellKey.includes('DATES') && !cellKey.includes('HEURE')))
                    return <td key={key} {...restTdProps}>{convertSmallDateTimeToLocalDateStr(cell.value)}</td>;

                  // Exemple : centrer la colonne si son ID contient 'center'
                  if (cellKey.includes('center'))
                    return <td key={key} {...restTdProps} className="center-col">{cell.render('Cell')}</td>;

                  // Exemple : styliser différemment certaines infos secondaires
                  if (cellKey.includes('secondaires'))
                    return <td key={key} {...restTdProps} className="infos-secondaires">{cell.render('Cell')}</td>;

                  // Cas par défaut
                  return <td key={key} {...restTdProps}>{cell.render('Cell')}</td>;
                })}
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </Styles>

    {/* Affichage pagination en bas si activée */}
    {noPagination === true ? <>{!noDisplayNbResult && <>{rows.length} résultats</>}</> : blocPourPagination}
  </>;
}

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  noPagination: PropTypes.bool,
  noDisplayNbResult: PropTypes.bool,
  noFilter: PropTypes.bool,
}
export default Table;

// syle de base : https://codesandbox.io/s/github/tannerlinsley/react-table/tree/v7/examples/basic
// si on voulait colorer la première col :  td:nth-child(1) { color: green !important; }
const Styles = styled.div`
  padding: 1rem;

  table {
    border-collapse: separate; // obligé pour pouvoir réussir les arrondis comme ci dessous
    border-spacing: 0;
    //border: 1px solid black;
     //width: 100%;
     min-width: 940px; //980px nok depasse dans l'accordéon a cause d'un padding left dans accordion-body...

    th,
    td {
      margin: 0;
      padding: 0.5rem 0.2rem; // avant il y avait 0.5 partout mais pas les padding-left: 0.5rem; padding-right: 0.5rem; sur les child.. 0 permet de ne pas avoir d'espace quand on veut colorrer les fons des cellules
    }
  }

  // class ci dessous inspirée du jsfiddle de https://stackoverflow.com/questions/10666573/tables-border-radius-does-not-function-as-expected :
  td {
    border: solid 1px grey;
    border-style: none none solid none;
    border-right-style: dotted;
    /*border-right-color: fcfcfc;*/
    padding: 10px;
  }

  th {
    border: solid 1px grey;
    border-style: solid none none none;
  }
  tr td:first-child, tr th:first-child {padding-left: 0.5rem;  border-top-left-radius: 10px; border-bottom-left-radius: 10px;}
  tr td:last-child{padding-right: 0.5rem; border-style: none solid solid none; border-top-right-radius: 10px; border-bottom-right-radius: 10px;}
  tr th:last-child { border-style: solid solid none none; border-top-right-radius: 10px; border-bottom-right-radius: 10px;}

  tr:first-child td, tr:first-child th { border-top-style: solid; }
  tr td:first-child, tr th:first-child { border-left-style: solid; }
  // pour colorier une ligne sur 2
  tr:nth-child(even)  {
    background-color: #f9f9f9;
  }  
  tr:nth-child(even) td:nth-child(even) {
    background-color: #fafafa;
  }
  tr:nth-child(odd) td:nth-child(even) {
    background-color: #fefefe;
  }
 /* issu de cet exemple : https://openclassrooms.com/forum/sujet/colorer-une-cellule-sur-deux-en-css
  td:nth-child(odd){
    background-color: #c0c0c0;
  }
  tr:nth-child(even) td:nth-child(even) {
    background-color: red;
  }
  tr:nth-child(odd) td:nth-child(even) {
    background-color: orange;
  }*/
  tr:hover {
    background-color:#eff1fc;
    opacity: 0.9;
  }
  /* 2 ci dessous obligés comme on fait tr:nth-child(even) td:nth-child(even) et tr:nth-child(odd) td:nth-child(even)*/
  tr:nth-child(even):hover td:nth-child(even) {
    background-color:#e1e4f7;
    opacity: 0.9;
  }
  tr:nth-child(odd):hover td:nth-child(even) {
    background-color:#e1e4f7;
    opacity: 0.9;
  }
`

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <span>
      Rechercher un ou des termes parmi la liste :{' '}
      <input
        value={value || ""}
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        style={{
          fontSize: '1.1rem',
          border: '1px lightgrey  solid',
        }}
      />
    </span>
  )
}
// auparavant était ci dessus en dessus de style : placeholder={`filtre sur les ${count} lignes...`}

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  )
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}


// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
  }) {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
      const options = new Set()
      preFilteredRows.forEach(row => {
        options.add(row.values[id])
      })
      return [...options.values()]
    }, [id, preFilteredRows])
  
    // Render a multi-select box
    return (
      <select
        value={filterValue}
        onChange={e => {
          setFilter(e.target.value || undefined)
        }}
      >
        <option value="">All</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    )
  }